<template>
    <div>
        <template v-if="loadingGetPlanPropositions">
            <div class="flex flex-col items-center justify-center py-20">
                <!-- Progress bar container -->
                <div class="w-full max-w-md mb-6">
                    <div class="bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div 
                            class="bg-gradient-to-r from-red-500 to-red-600 h-full transition-all duration-300 ease-linear"
                            :style="{ width: loadingProgress + '%' }"
                        ></div>
                    </div>
                </div>

                <!-- Loading message -->
                <div class="text-center">
                    <div class="flex items-center gap-3 mb-2">
                        <div class="flex gap-1">
                            <div class="w-2 h-2 bg-red-500 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
                            <div class="w-2 h-2 bg-red-500 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
                            <div class="w-2 h-2 bg-red-500 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
                        </div>
                        <span class="text-lg font-semibold text-gray-700">{{ loadingMessage }}</span>
                    </div>
                    <p class="text-sm text-gray-500">{{ roundedProgress }}%</p>
                </div>
            </div>
        </template>
        <template v-else>
            <template v-if="loadingPlan">
                <!-- Planification en cours -->
                <div class="flex flex-col items-center justify-center py-20">
                    <i class="fas fa-spinner fa-spin text-2xl text-gray-500"></i>
                    <h2 class="text-sm font-light text-gray-700 mt-3">Planification en cours...</h2>
                </div>
            </template>
            <template v-if="!loadingPlan">
                <template v-if="planPropositions.error">
                    <div class="text-red-500 font-bold">
                        <div>Une erreur est survenue lors de la recherche de créneaux disponibles.</div>
                        <div>
                            <button @click=""></button>
                        </div>
                    </div>
                </template>
                <div>
                    <div v-for="(slot, slotIndex) in orderSlots" :key="'best-slot-' + slotIndex" class="border border-gray-200 p-6 mb-4 rounded-lg hover:shadow-lg transition-shadow">
                        <div class="grid grid-cols-2 gap-4 text-sm">
                            <div class="space-y-3">
                                <div class="flex justify-between items-start">
                                    <div class="flex items-center gap-2">
                                        <div class="flex items-center gap-1">
                                            <span v-for="star in 5" :key="star" class="text-lg" :class="star <= slot.score ? 'text-yellow-400' : 'text-gray-300'">★</span>
                                        </div>
                                        <span class="text-sm font-semibold text-gray-600">({{ slot.score }}/5)</span>
                                    </div>
                                </div>
                                <div class="flex items-center gap-10">
                                    <p class="font-bold text-lg">
                                        <i class="fas fa-calendar-alt mr-1"></i> {{ slot.date | momentDate }}
                                    </p>
                                    <p class="font-bold text-lg">
                                        <i class="fas fa-clock mr-1"></i> {{ slot.start_time }} - {{ slot.end_time }}
                                    </p>
                                </div>
                                <div class="grid grid-cols-2 gap-4">
                                    <div class="bg-gray-50 border rounded-xl p-3">
                                        <p class="text-gray-600 text-xs uppercase tracking-wide mb-1">Raison</p>
                                        <p class="font-light text-xs">{{ slot.reasoning }}</p>
                                    </div>
                                    <div class="bg-gray-50 border rounded-xl p-3">
                                        <div>
                                            <p class="text-gray-600 text-xs uppercase tracking-wide mb-1">Technicien</p>
                                            <p class="font-semibold">{{ slot.technicien.prenom + ' ' + slot.technicien.nom }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="max-w-[150px] py-4">
                                    <div class="flex items-center gap-2">
                                        <i class="fas fa-map-pin"></i>
                                        <div class="border-b border-slate-600 grow"></div>
                                        <div class="">{{ slot.distance_km }} km</div>
                                        <div class="border-b border-slate-600 grow"></div>
                                        <i class="fas fa-truck"></i>
                                    </div>
                                </div>

                                <button @click="validateSlot(slot)" class="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                                    Valider ce créneau
                                </button>
                            </div>

                            <div>
                                <intervention-planning-day
                                    :date="slot.date"
                                    :interventions="slot.interventions"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </template>
    </div>
</template>

<script>
import moment from "moment";
import InterventionPlanningDay from './InterventionPlanningDay.vue';

export default {
    name: "InterventionPlanProposition",
    components: {
        InterventionPlanningDay
    },
    props: {
        interventionId: {
            type: Number,
            required: true
        }
    },
    watch: {
        interventionId(newId, oldId) {
            if (newId !== oldId) {
                this.getPlanPropositions();
            }
        }
    },
    data() {
        return {
            planPropositions: [],
            loadingGetPlanPropositions: false,
            loadingProgress: 0,
            loadingMessage: 'Recherche des meilleurs créneaux...',
            loadingInterval: null,
            loadingMessages: [
                'Recherche des meilleurs créneaux...',
                'Analyse des disponibilités...',
                'Optimisation des tournées...',
                'Calcul des scores...',
                'Finalisation...'
            ],
            currentMessageIndex: 0,
            loadingPlan: false
        }
    },
    computed: {
        orderSlots() {
            return this.planPropositions.slots ? [...this.planPropositions.slots].sort((a, b) => b.score - a.score) : []
        },
        roundedProgress() {
            return Math.round(this.loadingProgress);
        }
    },
    filters: {
        momentDate(date) {
            if (date) {
                return moment(date).format('DD/MM/YYYY');
            }
            return '-'
        },
        momentDateTime(date) {
            if (date) {
                return moment(date).format('DD/MM/YYYY HH:mm');
            }
            return '-'
        }
    },
    methods: {
        startLoadingAnimation() {
            this.loadingProgress = 0;
            this.currentMessageIndex = 0;
            this.loadingMessage = this.loadingMessages[0];
            
            // Update progress bar smoothly (100ms intervals for smooth animation)
            const duration = 13000; // 13 seconds
            const interval = 100; // Update every 100ms
            const increment = 100 / (duration / interval);
            
            this.loadingInterval = setInterval(() => {
                this.loadingProgress += increment;
                
                // Update message every 2 seconds (20% progress)
                const messageIndex = Math.floor(this.loadingProgress / 20);
                if (messageIndex !== this.currentMessageIndex && messageIndex < this.loadingMessages.length) {
                    this.currentMessageIndex = messageIndex;
                    this.loadingMessage = this.loadingMessages[messageIndex];
                }
                
                // Stop at 100%
                if (this.loadingProgress >= 100) {
                    this.loadingProgress = 100;
                    this.stopLoadingAnimation();
                }
            }, interval);
        },
        
        stopLoadingAnimation() {
            if (this.loadingInterval) {
                clearInterval(this.loadingInterval);
                this.loadingInterval = null;
            }
        },
        
        async getPlanPropositions() {
            this.loadingGetPlanPropositions = true;
            this.startLoadingAnimation();
            
            let response = await this.$request.post('/planning/best-slots2', {
                intervention_id: this.interventionId
            });
            
            if (response) {
                this.planPropositions = response;
            }
            
            this.stopLoadingAnimation();
            this.loadingGetPlanPropositions = false;
        },

        async validateSlot(slot) {
            this.loadingPlan = true
            await this.$request.post('/interventions/' + slot.intervention_id + '/planifier', {
                date_intervention: slot.date,
                heure_debut: slot.start_time,
                heure_fin: slot.end_time,
                technicien_id: slot.technicien.id
            });
            this.loadingPlan = false

            this.$emit('slot-validated');
        }
    },
    mounted() {
        this.getPlanPropositions();
    },
}
</script>

<style lang="scss" scoped>

</style>